import { similarText } from './bank-import.ts';
import { tidyName } from './names.ts';

// A statement the owner downloads from the bank's website (CSV) and adds to an account by hand —
// history older than the bank connection reaches, or an account without one. Pure: the app reads
// the file, shows what is new and what is already there, and creates only what the owner keeps.
//
// For now Lloyds (the same file comes from Halifax and Bank of Scotland):
//   Transaction Date,Transaction Type,Sort Code,Account Number,Transaction Description,Debit Amount,Credit Amount,Balance
//   26/09/2026,DEB,'77-19-12,12345678,TESCO STORES 3321 CD 6411,42.10,,1520.40
//
// Nothing is added twice (DECISIONS D-034): a row is the same as an entry already in the account
// when the amount is the same, the dates at most three days apart and the text similar — or the
// same day (±1) whatever the text. One entry answers for one row only, and the closest pairs are
// matched first. The other way round, a bank download takes over a statement entry (bank-import.ts).

/** Lloyds cuts the text of a statement row at 18 characters. */
const STATEMENT_TEXT_LIMIT = 18;
/** A statement text that may end in half a word. */
export const statementTextCut = (text: string | null | undefined) => (text?.length ?? 0) >= STATEMENT_TEXT_LIMIT;

export type StatementPaymentType = 'CARD' | 'TRANSFER' | 'STANDING_ORDER' | 'DIRECT_DEBIT' | 'CASH' | 'INTEREST' | 'OTHER';

export type StatementRow = {
  /** The row in the file; the header is row 1. */
  readonly line: number;
  readonly bookingDate: string;
  readonly amountMinor: bigint;
  /** The bank's text, as on the statement. */
  readonly text: string;
  /** A name made from it: "TESCO STORES 3321 CD 6411" → "Tesco Stores". */
  readonly name: string | null;
  readonly paymentType: StatementPaymentType;
  /** The balance after this entry. */
  readonly balanceMinor: bigint | null;
};

export type StatementProblem = { readonly line: number; readonly reason: 'DATE' | 'AMOUNT' | 'OTHER_ACCOUNT' };

export type Statement = {
  readonly bank: 'LLOYDS';
  /** The last four digits of the account number. */
  readonly accountMask: string | null;
  /** Oldest first; rows of one day keep the order the bank gave them. */
  readonly rows: StatementRow[];
  readonly problems: StatementProblem[];
};

/** Rows of a CSV file: quoted fields, commas and line breaks inside quotes, a BOM, CRLF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  for (let i = 0; i < source.length; i++) {
    const char = source[i]!;
    if (quoted) {
      if (char === '"' && source[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') quoted = false;
      else field += char;
    } else if (char === '"' && field === '') quoted = true;
    else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += char;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

const header = (cell: string) => cell.trim().toLowerCase().replace(/\s+/g, ' ');

/** "26/09/2026" → "2026-09-26"; null when it is not a real day. */
function ukDate(text: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!match) return null;
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date.toISOString().slice(0, 10);
}

/** "1,234.50" or "£42.10" → 123450n; "" → 0n; null when it is not an amount in pounds and pence. */
function pence(text: string): bigint | null {
  const plain = text.trim().replace(/[£,\s]/g, '');
  if (plain === '') return 0n;
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(plain);
  if (!match) return null;
  const minor = BigInt(match[2]!) * 100n + BigInt((match[3] ?? '').padEnd(2, '0'));
  return match[1] ? -minor : minor;
}

/** Lloyds' codes for the kind of payment. */
function lloydsPaymentType(code: string): StatementPaymentType {
  switch (code.trim().toUpperCase()) {
    case 'DEB':
    case 'EFT':
      return 'CARD';
    case 'DD':
      return 'DIRECT_DEBIT';
    case 'SO':
      return 'STANDING_ORDER';
    case 'CPT':
    case 'CSH':
      return 'CASH';
    case 'INT':
    case 'BNS':
      return 'INTEREST';
    case 'BGC':
    case 'BP':
    case 'FPI':
    case 'FPO':
    case 'MPI':
    case 'MPO':
    case 'TFR':
    case 'IB':
    case 'PAY':
    case 'DEP':
      return 'TRANSFER';
    default:
      return 'OTHER';
  }
}

/** The merchant or payee without references and card numbers: "TESCO STORES 3321 CD 6411" → "Tesco Stores". */
export function statementName(text: string): string | null {
  const words = text
    .replace(/\*/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((word) => (word.match(/\d/g)?.length ?? 0) < 2);
  // Lloyds ends card payments with "CD 6411": the card, not the merchant.
  while (words.length > 1 && /^CD$/i.test(words.at(-1)!)) words.pop();
  return tidyName(words.join(' '));
}

/** A statement we know how to read, or null. */
export function readStatement(text: string): Statement | null {
  const table = parseCsv(text).filter((cells) => cells.some((cell) => cell.trim() !== ''));
  const headerAt = table.findIndex((cells) => cells.map(header).includes('transaction date'));
  if (headerAt < 0) return null;
  const names = table[headerAt]!.map(header);
  const column = (name: string) => names.indexOf(name);
  const at = {
    date: column('transaction date'),
    type: column('transaction type'),
    account: column('account number'),
    text: column('transaction description'),
    debit: column('debit amount'),
    credit: column('credit amount'),
    balance: column('balance'),
  };
  if (at.text < 0 || at.debit < 0 || at.credit < 0) return null;

  const body = table.slice(headerAt + 1).map((cells, index) => ({ cells, line: headerAt + index + 2 }));
  // A file holds one account; rows of another (a joint download) are left out and said so.
  const numbers = new Map<string, number>();
  for (const { cells } of body) {
    const number = (cells[at.account] ?? '').replace(/\D/g, '');
    if (number) numbers.set(number, (numbers.get(number) ?? 0) + 1);
  }
  const account = [...numbers.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  const rows: StatementRow[] = [];
  const problems: StatementProblem[] = [];
  for (const { cells, line } of body) {
    const cell = (index: number) => (index >= 0 ? (cells[index] ?? '') : '');
    const number = cell(at.account).replace(/\D/g, '');
    if (account && number && number !== account) {
      problems.push({ line, reason: 'OTHER_ACCOUNT' });
      continue;
    }
    const bookingDate = ukDate(cell(at.date));
    if (!bookingDate) {
      problems.push({ line, reason: 'DATE' });
      continue;
    }
    const debit = pence(cell(at.debit));
    const credit = pence(cell(at.credit));
    const balance = at.balance >= 0 && cell(at.balance).trim() !== '' ? pence(cell(at.balance)) : null;
    if (debit === null || credit === null || (debit === 0n && credit === 0n)) {
      problems.push({ line, reason: 'AMOUNT' });
      continue;
    }
    const abs = (minor: bigint) => (minor < 0n ? -minor : minor);
    const description = cell(at.text).trim().replace(/\s+/g, ' ');
    rows.push({
      line,
      bookingDate,
      amountMinor: abs(credit) - abs(debit),
      text: description,
      name: statementName(description),
      paymentType: lloydsPaymentType(cell(at.type)),
      balanceMinor: balance,
    });
  }
  // Lloyds lists the newest first. The balances tell the order even within one day: the newer row's
  // balance is the older one's plus its own amount.
  const [first, second] = rows;
  const newestFirst =
    first && second && first.balanceMinor !== null && second.balanceMinor !== null
      ? first.balanceMinor - first.amountMinor === second.balanceMinor
      : rows.length > 1 && rows[0]!.bookingDate > rows.at(-1)!.bookingDate;
  if (newestFirst) rows.reverse();
  return { bank: 'LLOYDS', accountMask: account ? account.slice(-4) : null, rows, problems };
}

export type ExistingEntry = {
  readonly id: string;
  readonly bookingDate: string;
  readonly amountMinor: bigint;
  /** Its bank text, name and description. */
  readonly texts: readonly (string | null)[];
};

/** The entry already in the account for a statement row; `sure` when the texts agree as well. */
export type StatementMatch = { readonly existingId: string; readonly sure: boolean } | null;

const DAY_MS = 86_400_000;
const daysApart = (a: string, b: string) => Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / DAY_MS;

/** For each row, the entry of the same account it repeats — one entry for one row, closest pairs first. */
export function matchStatementRows(rows: readonly Pick<StatementRow, 'bookingDate' | 'amountMinor' | 'text' | 'name'>[], existing: readonly ExistingEntry[]): StatementMatch[] {
  const byAmount = new Map<bigint, ExistingEntry[]>();
  for (const entry of existing) byAmount.set(entry.amountMinor, [...(byAmount.get(entry.amountMinor) ?? []), entry]);
  const pairs: { row: number; entry: ExistingEntry; score: number; sure: boolean }[] = [];
  rows.forEach((row, index) => {
    for (const entry of byAmount.get(row.amountMinor) ?? []) {
      const days = daysApart(entry.bookingDate, row.bookingDate);
      if (days > 3) continue;
      const sure = entry.texts.some((text) => similarText(text, row.text) || similarText(text, row.name));
      if (!sure && days > 1) continue;
      pairs.push({ row: index, entry, score: (sure ? 0 : 10) + days, sure });
    }
  });
  pairs.sort((a, b) => a.score - b.score || a.row - b.row || (a.entry.id < b.entry.id ? -1 : 1));
  const matches: StatementMatch[] = rows.map(() => null);
  const taken = new Set<string>();
  for (const pair of pairs) {
    if (matches[pair.row] || taken.has(pair.entry.id)) continue;
    matches[pair.row] = { existingId: pair.entry.id, sure: pair.sure };
    taken.add(pair.entry.id);
  }
  return matches;
}
